import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useBabyStore, useActiveBaby } from '@/stores/useBabyStore';
import { ChatWindow } from '@/components/chat/ChatWindow';
import { FullPageState } from '@/components/ui/PageState';
import { api } from '@/lib/api-client';
import { isGerman } from '@/i18n/locale';
import type { Message } from '@/types';

// Chat page — entry point at /chat (new conversation) and /chat/:sessionId
// (resume an existing one).
//
// When :sessionId is present we fetch the session + messages, then pass
// them into ChatWindow as initialSessionId + initialMessages. ChatWindow
// then uses those instead of starting fresh.
//
// If the session belongs to a different baby than the currently active
// one, we silently switch the active baby to match — otherwise the
// system prompt would target the wrong baby's age and feed type.
export default function Chat() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { babies, loaded, load, setActive } = useBabyStore();
  const baby = useActiveBaby();
  const de = isGerman();

  const [resumeData, setResumeData] = useState<
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; sessionId: string; messages: Message[]; babyId: string | null }
    | { status: 'not_found' }
    | { status: 'error'; message: string }
  >({ status: 'idle' });

  useEffect(() => {
    if (!loaded) load();
  }, [loaded, load]);

  // Load the session referenced in the URL, if any.
  useEffect(() => {
    if (!sessionId) {
      setResumeData({ status: 'idle' });
      return;
    }
    setResumeData({ status: 'loading' });
    let cancelled = false;
    (async () => {
      try {
        const res = await api.sessions.get(sessionId);
        if (cancelled) return;
        const sess = res.session as { id: string; baby_id: string | null };
        setResumeData({
          status: 'ready',
          sessionId: sess.id,
          messages: res.messages,
          babyId: sess.baby_id,
        });
      } catch (err) {
        if (cancelled) return;
        const message = (err as { status?: number }).status === 404
          ? 'not_found'
          : ((err as Error).message ??
            (de ? 'Dieses Gespräch konnte nicht geladen werden.' : 'Could not load this conversation.'));
        if (message === 'not_found') {
          setResumeData({ status: 'not_found' });
        } else {
          setResumeData({ status: 'error', message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, de]);

  // Switch active baby to match the resumed session's baby. Only acts
  // when we have a session loaded AND the active baby differs.
  useEffect(() => {
    if (resumeData.status !== 'ready') return;
    if (!resumeData.babyId) return;
    if (baby?.id === resumeData.babyId) return;
    const targetBaby = babies.find((b) => b.id === resumeData.babyId);
    if (targetBaby) {
      void setActive(targetBaby.id);
    }
  }, [resumeData, babies, baby?.id, setActive]);

  if (!loaded) return <FullPageState />;

  if (babies.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!baby) {
    return <FullPageState busy={false} label={de ? 'Kein Baby ausgewählt.' : 'No baby selected.'} />;
  }

  // /chat/:sessionId path — wait for the session fetch
  if (sessionId) {
    if (resumeData.status === 'loading' || resumeData.status === 'idle') {
      return <FullPageState label={de ? 'Gespräch wird geladen…' : 'Loading conversation…'} />;
    }
    if (resumeData.status === 'not_found') {
      return <Navigate to="/chat/history" replace />;
    }
    if (resumeData.status === 'error') {
      return (
        <FullPageState busy={false} label={resumeData.message}>
          <Link to="/chat" className="btn-primary text-sm mt-4 py-2 px-4">
            {de ? 'Neues Gespräch starten' : 'Start a fresh chat'}
          </Link>
        </FullPageState>
      );
    }
    // status === 'ready' — render the resumed chat. Use a key tied to the
    // session id so the ChatWindow remounts cleanly when switching sessions.
    return (
      <ChatWindow
        key={resumeData.sessionId}
        baby={baby}
        initialSessionId={resumeData.sessionId}
        initialMessages={resumeData.messages}
      />
    );
  }

  // /chat (no session) — fresh conversation
  return <ChatWindow key={baby.id} baby={baby} />;
}
